// Alle Leads (lokale Kopie): Titel mit Smart-View-Wechsler, Bedienleiste mit Textsuche im Namen, Statusfilter, Sortierung nach bis zu drei
// Feldern (auch berechneten wie Anzahl Anrufe, gemerkt je Nutzer), Spaltenwahl (gemerkt je Nutzer), Markieren (Kästchen, x), j/k/Enter,
// Nachladen beim Scrollen.
// Runde 5 (Vorarbeit): zwei Plätze unter dem Listenkopf, `FilterZeile` (Agent 5A) und `Bedienleiste` (Agent 5B), dazu der
// Filter-Entwurf (hält die Seite einen, kommen die Treffer aus `filterApi.treffer`, sonst aus `listenAnsichtenApi.zeilenLeads`).
// Vertrag: components/smartview/listenVertrag.ts.
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { type LeadZeile, type SmartViewKurz } from "../api";
import { filterApi, type FilterEntwurf } from "../api/filter";
import { listenAnsichtenApi } from "../api/listenAnsichten";
import { Auswahl } from "../components/Auswahl";
import { Laden, Leer } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { LeadTabelle, type Spalte } from "../components/LeadTabelle";
import { useSpaltenLayout } from "../components/spaltenLayout";
import { leadZelle } from "../components/LeadZelle";
import { Bedienleiste } from "../components/smartview/Bedienleiste";
import { ListenTitel } from "../components/smartview/BedienleisteWechsler";
import {
  ALIASE_STANDARD,
  gleicheSortierung,
  kanonisch,
  kopfLang,
  sortKlick,
  sortVonText,
  sortZuText,
  spaltenBereinigen,
  type SortEintrag,
} from "../components/smartview/BedienleisteLogik";
import { berechneteZelle } from "../components/smartview/SpaltenZelle";
import { FilterZeile } from "../components/smartview/FilterZeile";
import { ListenKopfRechts } from "../components/smartview/ListenKopfRechts";
import { ListenSchluessel, type ListenSpalte } from "../components/smartview/listenVertrag";
import { KONTAKTWEGE } from "../components/listenSpalten";
import { BerechnungsZelle } from "../components/liste/BerechnungsZelle";
import { LimitSteuerung } from "../components/liste/LimitSteuerung";
import { limitVon, mitLimit, sortAlsAbfrage, sortBeschreibung } from "../components/liste/berechnungModell";
import { useListenBerechnung } from "../components/liste/useListenBerechnung";
import { useUngekuerzteZahl } from "../components/liste/useUngekuerzteZahl";
import { useFilterKatalog } from "../components/smartview/filterDaten";
import { feldSuchAbfrage, gleicheAbfrage, kanonisch as abfrageKanonisch, standardAbfrage } from "../components/smartview/filterModell";
import type { Abfrage } from "../api/filter";
import { listeLesen, listeSetzen } from "../daten/liste";
import { useBerechneteWerte, useFelderKatalog, useGemerkteAnsicht } from "../daten/listenAnsichtStore";
import { useMeta } from "../daten/meta";
import { useSeitenKopf } from "../daten/seite";

// Standard, solange der Katalog nicht da ist: Name, Status, Telefon/E-Mail als Symbole, Kontakt, Erstellt, Geändert.
const STANDARD_SPALTEN = ["display_name", "status_id", KONTAKTWEGE, "primary_contact_name", "date_created", "date_updated"];
const NAMEN: Record<string, string> = { display_name: "Name", status_id: "Status", primary_contact_name: "Kontakt", date_created: "Erstellt", date_updated: "Geändert" };
const STANDARD_SORT: SortEintrag[] = [{ key: "date_updated", ab: true }];
const EINFACHE_SORT = new Set(["date_updated", "date_created", "display_name", "status_id"]);

const AUSWAHL = "__auswahl";
const AUSWAHL_SPALTE: Spalte = { key: AUSWAHL, label: "", kopfTitel: "Auswahl für Massen-Mail und Massenbearbeitung", klasse: "sv-auswahlspalte" };

function spaltenKlasse(key: string, typ: string | undefined): string | undefined {
  if (key === KONTAKTWEGE) return "spalte-wege";
  if (key === "display_name") return "spalte-name";
  if (key === "status_id") return "spalte-status";
  if (key === "primary_phone") return "spalte-telefon";
  if (key === "primary_email") return "spalte-mail";
  const lang = kopfLang(key) ? " spalte-lang" : "";
  if (typ === "zeit" || typ === "datum") return `spalte-datum${lang}`;
  if (typ) return `spalte-feld${lang}`;
  return undefined;
}

export default function Leads() {
  useSeitenKopf([{ label: "Leads" }]);
  const meta = useMeta();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const statusIds = useMemo(() => (params.get("status") ?? "").split(",").filter(Boolean), [params]);
  const sortParam = params.get("sort");
  const [eingabe, setEingabe] = useState(q);
  const [zeilen, setZeilen] = useState<LeadZeile[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [laedtMehr, setLaedtMehr] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const [fokus, setFokus] = useState(0);
  const [markiert, setMarkiert] = useState<Set<string>>(() => new Set());
  // Filter-Entwurf (Agent 5A setzt ihn über die FilterZeile); null = Liste mit Suchfeld und Statusfilter wie bisher
  // Lupe an einem Lead-Feld (linke Spalte): /leads?feld=<id>&typ=<typ>&wert=<JSON> öffnet die Liste mit genau diesem Filter
  const [entwurf, setEntwurf] = useState<FilterEntwurf | null>(() => {
    const feld = params.get("feld");
    if (!feld) return null;
    try {
      const sq = feldSuchAbfrage(feld, params.get("typ") ?? "text", JSON.parse(params.get("wert") ?? "null"));
      return sq ? ({ s_query: sq } as FilterEntwurf) : null;
    } catch {
      return null;
    }
  });
  const generation = useRef(0);
  // zählt jedes fertige Laden der Liste: die Berechnungszeile rechnet dann neu (Werte können sich geändert haben)
  const [ladeStand, setLadeStand] = useState(0);

  // --- Gemerkte Ansicht, Katalog, Spalten (Agent 5B) ---
  const katalog = useFelderKatalog();
  const gemerkt = useGemerkteAnsicht(ListenSchluessel.leads);
  const aliase = katalog?.aliase ?? ALIASE_STANDARD;
  const kan = useCallback((k: string) => kanonisch(k, aliase), [aliase]);
  const katalogMap = useMemo(() => new Map((katalog?.felder ?? []).map((f) => [f.key, f])), [katalog]);
  const waehlbar: ListenSpalte[] = useMemo(
    () => (katalog?.felder ?? []).map((f) => ({ key: f.key, label: f.label, gruppe: f.gruppe, sortierbar: f.sortierbar, fest: f.fest })),
    [katalog],
  );
  const standardKeys = useMemo(() => (katalog?.standard ?? STANDARD_SPALTEN).map(kan), [katalog, kan]);
  const eigeneSpalten = gemerkt.ansicht?.spalten ?? null;
  const gezeigt = useMemo(() => {
    if (!eigeneSpalten || !katalog) return standardKeys;
    const keys = spaltenBereinigen(eigeneSpalten, aliase, new Set(katalog.felder.map((f) => f.key)), ["display_name"]);
    return keys.length ? keys : standardKeys;
  }, [eigeneSpalten, katalog, standardKeys, aliase]);
  const spalten: Spalte[] = useMemo(
    () =>
      gezeigt.map((k): Spalte => {
        const f = katalogMap.get(k);
        if (k === KONTAKTWEGE) return { key: k, label: "", klasse: "spalte-wege", kopfTitel: "Telefon und E-Mail des Hauptkontakts" };
        return { key: k, label: f?.label ?? NAMEN[k] ?? k, sortierbar: katalog ? f?.sortierbar !== false : k in NAMEN, klasse: spaltenKlasse(k, f?.typ ?? (k === "date_created" || k === "date_updated" ? "zeit" : undefined)) };
      }),
    [gezeigt, katalogMap, katalog],
  );
  const layout = useSpaltenLayout(gemerkt, gezeigt, (keys) => gemerkt.aendern({ spalten: keys }));

  // --- Sortierung: Adresse (?sort=-num_calls,display_name) vor gemerkter Wahl vor Standard (zuletzt geändert zuerst) ---
  const eigeneSort: SortEintrag[] = useMemo(() => {
    const roh = sortParam !== null ? sortVonText(sortParam, aliase) : (gemerkt.ansicht?.sortierung ?? []).map((s) => ({ key: kan(s.key), ab: s.ab }));
    return katalog ? roh.filter((s) => katalogMap.get(s.key)?.sortierbar) : roh;
  }, [sortParam, gemerkt.ansicht?.sortierung, aliase, kan, katalog, katalogMap]);
  const wirksameSort = eigeneSort.length ? eigeneSort : STANDARD_SORT;
  const sortText = sortZuText(wirksameSort);

  const setParam = useCallback(
    (k: string, v: string | null) => {
      setParams(
        (alt) => {
          const neu = new URLSearchParams(alt);
          if (v) neu.set(k, v);
          else neu.delete(k);
          return neu;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  const sortSetzen = (neu: SortEintrag[]) => {
    const standard = neu.length === 0 || gleicheSortierung(neu, STANDARD_SORT);
    setParam("sort", standard ? null : sortZuText(neu));
    gemerkt.aendern({ sortierung: standard ? null : neu });
  };
  const kopfSort = (key: string) => {
    const typ = katalogMap.get(key)?.typ;
    const zuerstAb = typ === "zeit" || typ === "datum" || typ === "zahl";
    // ohne eigene Wahl steht die Liste schon „Geändert absteigend“: ein Klick darauf dreht um
    if (!eigeneSort.length && key === STANDARD_SORT[0].key) return sortSetzen([{ key, ab: !STANDARD_SORT[0].ab }]);
    sortSetzen(sortKlick(eigeneSort, key, zuerstAb));
  };

  // --- Berechnete Spalten ---
  const berechneteKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const k of gezeigt) if (katalogMap.get(k)?.berechnet) keys.add(k);
    return [...keys];
  }, [gezeigt, katalogMap]);
  const trefferIds = useMemo(() => zeilen.map((z) => z.id), [zeilen]);
  const berechnet = useBerechneteWerte(trefferIds, berechneteKeys);
  const zelleExtra = (z: LeadZeile, key: string): ReactNode | undefined => {
    const f = katalogMap.get(key);
    if (!f?.berechnet) return undefined;
    return berechneteZelle(key, f.typ, berechnet.wert(z.id, key), meta);
  };

  // Suche verzögert in die Adresse schreiben
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (eingabe.trim() !== q) setParam("q", eingabe.trim() || null);
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eingabe]);

  // Erst laden, wenn die gemerkte Sortierung da ist (sonst lädt die Liste zweimal) und, bei Sortierung nach berechneten oder eigenen Feldern,
  // der Katalog (er sortiert Felder aus, die es nicht mehr gibt, sonst antwortet die Route mit 400)
  const bereit = (gemerkt.geladen || sortParam !== null) && (katalog !== null || eigeneSort.every((s) => EINFACHE_SORT.has(s.key)));
  // --- Berechnungszeile und Limit (Agent 10B, Runde 10) ---
  const typVon = useCallback((k: string) => katalogMap.get(k)?.typ, [katalogMap]);
  const berechnungsZiel = useMemo(
    () => (!bereit || fehler ? null : entwurf ? ({ s_query: entwurf.s_query } as const) : ({ liste: true, q: q || undefined, status_id: statusIds.length ? statusIds : undefined } as const)),
    [bereit, fehler, entwurf, q, statusIds],
  );
  const berechnung = useListenBerechnung({ gemerkt, typVon, gezeigt, ziel: berechnungsZiel, stempel: ladeStand });
  const standard = useMemo<Abfrage>(() => standardAbfrage("lead") as Abfrage, []);
  const aktuelleAbfrage = entwurf?.s_query ?? standard;
  const limit = limitVon(aktuelleAbfrage);
  const ungekuerzt = useUngekuerzteZahl(aktuelleAbfrage, limit, ladeStand);
  const { katalog: filterKatalog } = useFilterKatalog("lead");

  /** Entwurf setzen; ein Entwurf ohne Bedingung und ohne Limit ist keiner (die Liste lädt dann wieder über Suche und Status). */
  const entwurfSetzen = useCallback(
    (neu: FilterEntwurf | null) => {
      if (!neu) return setEntwurf(null);
      let sq = neu.s_query;
      // Die Liste ist nach der Wahl in der Bedienleiste sortiert; der Entwurf soll genauso sortieren (und das Limit die gleichen ersten N wählen)
      const sort = filterKatalog ? sortAlsAbfrage(wirksameSort, filterKatalog.sortierfelder) : null;
      if (sort && abfrageKanonisch(sort) !== abfrageKanonisch(sq.sort ?? [])) sq = { ...sq, sort } as Abfrage;
      const ohneSort = { ...sq, sort: standard.sort } as Abfrage;
      setEntwurf(gleicheAbfrage(ohneSort, standard) ? null : { s_query: sq });
    },
    [filterKatalog, wirksameSort, standard],
  );
  // Sortierung in der Bedienleiste geändert, während ein Entwurf die Liste bestimmt: Entwurf zieht nach
  useEffect(() => {
    if (!entwurf || !filterKatalog) return;
    const sort = sortAlsAbfrage(wirksameSort, filterKatalog.sortierfelder);
    if (sort && abfrageKanonisch(sort) !== abfrageKanonisch(entwurf.s_query.sort ?? [])) setEntwurf({ s_query: { ...entwurf.s_query, sort } as Abfrage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortText, filterKatalog]);
  const limitSetzen = (n: number | null) => entwurfSetzen({ s_query: mitLimit(aktuelleAbfrage, n) });

  const laden = useCallback(async () => {
    if (!bereit) return;
    const gen = ++generation.current;
    setLaedt(true);
    setFehler(null);
    try {
      const r = entwurf
        ? await filterApi.treffer({ s_query: entwurf.s_query, limit: 100 })
        : await listenAnsichtenApi.zeilenLeads({ limit: 100, sort: sortText, status_id: statusIds, q: q || undefined });
      if (gen !== generation.current) return;
      setZeilen(r.data);
      setTotal(r.total);
      setCursor(r.has_more ? r.cursor : null);
      setLadeStand((n) => n + 1);
      const k = listeLesen();
      const i = k?.quelle === "leads" && k.fokus ? r.data.findIndex((z) => z.id === k.fokus) : -1;
      setFokus(i >= 0 ? i : 0);
    } catch (e) {
      if (gen === generation.current) setFehler(e);
    } finally {
      if (gen === generation.current) setLaedt(false);
    }
  }, [sortText, statusIds, q, entwurf, bereit]);

  useEffect(() => {
    void laden();
  }, [laden]);

  // andere Suche oder anderer Filter: Markierung gilt nicht mehr
  useEffect(() => setMarkiert(new Set()), [q, statusIds, entwurf]);

  const mehr = async () => {
    if (!cursor || laedtMehr) return;
    setLaedtMehr(true);
    const gen = generation.current;
    try {
      const r = entwurf
        ? await filterApi.treffer({ s_query: entwurf.s_query, limit: 100, cursor })
        : await listenAnsichtenApi.zeilenLeads({ limit: 100, cursor, sort: sortText, status_id: statusIds, q: q || undefined });
      if (gen !== generation.current) return;
      setZeilen((z) => {
        const bekannt = new Set(z.map((x) => x.id));
        return [...z, ...r.data.filter((x) => !bekannt.has(x.id))];
      });
      setCursor(r.has_more ? r.cursor : null);
    } catch (e) {
      setFehler(e);
    } finally {
      setLaedtMehr(false);
    }
  };

  const pfad = `/leads${params.toString() ? `?${params.toString()}` : ""}`;
  const oeffnen = (z: LeadZeile) => {
    listeSetzen({ quelle: "leads", titel: "Leads", pfad, ids: zeilen.map((x) => x.id), fokus: z.id });
    navigate(`/lead/${encodeURIComponent(z.id)}`);
  };

  const statusOptionen = (meta.meta?.lead_statuses ?? []).map((s) => ({ wert: s.id, label: s.label, farbe: s.color }));

  // Nach "Save as" (Agent 5A): die neue Smart View öffnen
  const gespeichert = (v: SmartViewKurz) => navigate(`/smart-views/${encodeURIComponent(v.id)}`);

  return (
    <div className="liste-seite">
      <div className="liste-kopf">
        <ListenTitel titel="Leads" aktuell="leads" />
        <div className="liste-kopf__rechts">
          <ListenKopfRechts
            titel="Leads"
            // Mit Entwurf wirken Massenaktionen nur auf die geladenen Treffer (sie sind die, die der Nutzer sieht)
            ziel={entwurf ? { lead_ids: zeilen.slice(0, 2000).map((z) => z.id) } : { liste: true, q: q || undefined, status_id: statusIds.length ? statusIds : undefined }}
            trefferZahl={entwurf ? zeilen.length : total}
            markierte={[...markiert]}
            onAlleMarkieren={zeilen.length ? () => setMarkiert(new Set(zeilen.map((z) => z.id))) : undefined}
            onMarkierungAufheben={() => setMarkiert(new Set())}
            filterJson={entwurf?.s_query ?? (q || statusIds.length ? { q: q || undefined, status_id: statusIds.length ? statusIds : undefined } : undefined)}
            onNeuLaden={() => void laden()}
            entwurf={entwurf}
            onGespeichert={gespeichert}
          />
        </div>
      </div>
      {/* Platz 5A: Filterzeile ("› Filters [n]", Bedingungen, Speichern als Smart View) */}
      <FilterZeile art="leads" trefferZahl={total} entwurf={entwurf} onEntwurf={entwurfSetzen} onGespeichert={gespeichert} />
      {/* Platz 5B: Bedienleiste (Zähler, Sortierung, Spalten, Auswahl, Textsuche); der Statusfilter steht vorerst davor */}
      <Bedienleiste
        liste={ListenSchluessel.leads}
        einheit={{ eins: "Lead", viele: "Leads" }}
        trefferZahl={total}
        geladen={zeilen.length}
        spalten={waehlbar}
        gezeigt={gezeigt}
        onSpalten={(keys) => gemerkt.aendern({ spalten: keys })}
        spaltenStandard={standardKeys}
        sortFelder={waehlbar}
        sortierung={eigeneSort}
        onSortierung={sortSetzen}
        sortStandard={STANDARD_SORT}
        sortStandardName="Standard"
        hinweis={
          <LimitSteuerung
            limit={limit}
            gesamt={ungekuerzt}
            reihenfolge={sortBeschreibung(wirksameSort, (k) => katalogMap.get(k)?.label ?? NAMEN[k] ?? k)}
            onSetzen={limitSetzen}
            moeglich={!!filterKatalog}
          />
        }
        suche={{ wert: eingabe, onAendern: setEingabe, platzhalter: "Name enthält …" }}
        auswahl={{
          anzahl: markiert.size,
          onAlleMarkieren: zeilen.length ? () => setMarkiert(new Set(zeilen.map((z) => z.id))) : undefined,
          onAufheben: () => setMarkiert(new Set()),
        }}
        vorn={
          <div className="liste-leiste__filter">
            <Auswahl
              optionen={statusOptionen}
              wert={statusIds}
              mehrfach
              leerErlaubt
              platzhalter="Alle Status"
              breite={260}
              onWahl={(w) => setParam("status", (w as string[]).join(",") || null)}
            />
          </div>
        }
      />
      {fehler ? <FehlerBox fehler={fehler} onNochmal={() => void laden()} /> : null}
      {laedt && !zeilen.length ? (
        <Laden />
      ) : !fehler && !zeilen.length ? (
        <Leer
          titel={entwurf ? "Keine Leads für diesen Filter." : "Keine Leads gefunden."}
          text={entwurf ? "Filter anpassen oder verwerfen." : q || statusIds.length ? "Filter anpassen oder leeren." : "Die lokale Kopie ist noch leer. Der Abgleich mit Close füllt sie."}
        />
      ) : zeilen.length ? (
        <LeadTabelle
          klasse={spalten.filter((s) => s.key !== KONTAKTWEGE).length >= 7 ? "tabelle--dicht" : undefined}
          spalten={[AUSWAHL_SPALTE, ...spalten]}
          zeilen={zeilen}
          fokus={Math.min(fokus, zeilen.length - 1)}
          setFokus={setFokus}
          onOeffnen={(z) => oeffnen(z)}
          sort={wirksameSort[0] ?? null}
          onSort={kopfSort}
          onEnde={cursor ? () => void mehr() : undefined}
          auswahl={{ markiert, onAendern: setMarkiert }}
          breiten={layout.breiten}
          onBreite={layout.onBreite}
          onVerschieben={layout.onVerschieben}
          berechnung={(s) => (
            <BerechnungsZelle
              label={s.label || (katalogMap.get(s.key)?.label ?? s.key)}
              typ={typVon(s.key)}
              funktion={berechnung.wahl[s.key]}
              ergebnis={berechnung.ergebnisse[s.key]}
              laedt={berechnung.laedt}
              anzahl={berechnung.anzahl}
              gekappt={berechnung.gekappt}
              onWaehlen={(f) => berechnung.setzen(s.key, f)}
            />
          )}
          zelle={(z, s) =>
            leadZelle(
              z,
              s.key,
              meta,
              (e: MouseEvent<HTMLAnchorElement>) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                oeffnen(z);
              },
              (key) => zelleExtra(z, key),
            )
          }
          fuss={
            cursor ? (
              <div className="tabelle__mehr">
                <button type="button" className="knopf" disabled={laedtMehr} onClick={() => void mehr()}>
                  {laedtMehr ? "Lädt …" : "Weitere laden"}
                </button>
              </div>
            ) : null
          }
        />
      ) : null}
    </div>
  );
}
